import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { qualityTemplates } from '../../data/qualityTemplates';
import { company } from '../../data/company';
import { ProCustomer } from '../../types/pro';
interface NewQualityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (report: Omit<QualityReport, 'id'>) => void;
  customers: ProCustomer[];
  nextReportId: string;
  responsible: {
    name: string;
    email: string;
    phone: string;
  };
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NewQualityReportModal({
  isOpen,
  onClose,
  onCreate,
  customers,
  nextReportId,
  responsible
}: NewQualityReportModalProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [manualCustomer, setManualCustomer] = useState('');
  const [templateId, setTemplateId] = useState(qualityTemplates[0].id);
  const reset = () => {
    setTitle('');
    setCustomerId('');
    setManualCustomer('');
    setTemplateId(qualityTemplates[0].id);
  };
  const selectedCustomer = customers.find(c => c.id === customerId);
  const customerName = selectedCustomer?.name || manualCustomer.trim();
  const canSubmit = title.trim().length > 0 && customerName.length > 0;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const template = qualityTemplates.find(t => t.id === templateId) ?? qualityTemplates[0];
    onCreate({
      reportId: nextReportId,
      title: title.trim(),
      status: 'Åben',
      visibleToCustomer: false,
      description: '',
      customer: {
        name: customerName,
        address: [selectedCustomer?.address, selectedCustomer?.city].filter(Boolean).join(', ')
      },
      quote: {
        reference: '',
        status: 'Ikke tilknyttet',
        amount: 0
      },
      project: {
        reference: '',
        title: title.trim()
      },
      responsible,
      company: {
        ...company
      },
      sections: template.build(),
      photos: []
    });
    reset();
    onClose();
  };
  return <AnimatePresence data-id="element-797" data-mp="12v9gha103nrv">
      {isOpen && <div role="dialog" aria-modal="true" aria-labelledby="new-ks-title" className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-798" data-mp="y3rgao6sxr7y">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-stone-900/40" data-id="element-799" data-mp="p3uo4cl7ea85" />

          <motion.form onSubmit={handleSubmit} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl" data-id="element-800" data-mp="b3tkipdk0ooz">
            <header className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4" data-id="element-801" data-mp="3sd99enf4qbyz">
              <div data-id="element-802" data-mp="10wqs38ewcuv">
                <h2 id="new-ks-title" className="text-lg font-bold tracking-tight text-stone-900" data-id="element-803" data-mp="11pyb6f736e2">
                  Ny kvalitetssikring
                </h2>
                <p className="mt-0.5 text-sm text-stone-500" data-id="element-804" data-mp="34iolhpdxnawr">
                  Får nummer{' '}
                  <span className="font-mono font-medium text-brand-700" data-id="element-805" data-mp="39h9nnucqw4nu">
                    {nextReportId}
                  </span>
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Luk" className="-mr-1 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-806" data-mp="3ihobb0d6lvui">
                <XIcon className="h-5 w-5" data-id="element-807" data-mp="w8722mjlctqd" />
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5" data-id="element-808" data-mp="r9vpi41ww54g">
              <label className="block" data-id="element-809" data-mp="rbyrlmgc9opj">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-810" data-mp="159n8ykb76dpu">
                  Hvad handler opgaven om?{' '}
                  <span className="text-red-500" data-id="element-811" data-mp="116i9yrqbsu5y">*</span>
                </span>
                <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="f.eks. Asbesttag nedtagning og fjernelse" className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-812" data-mp="1f69w5fqzxgv1" />
              </label>

              <div data-id="element-813" data-mp="1xyc5e61b5uv9">
                <label className="block" data-id="element-814" data-mp="1xvpwa1yrr7ax">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-815" data-mp="1szr5k7pyxd7c">
                    Kunde <span className="text-red-500" data-id="element-816" data-mp="2ckhudc88olxn">*</span>
                  </span>
                  <select value={customerId} onChange={e => setCustomerId(e.target.value)} className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-817" data-mp="2hjm2dzjfgdme">
                    <option value="" data-id="element-818" data-mp="1tt8iiwazxl2w">Vælg kunde…</option>
                    {customers.map(customer => <option key={customer.id} value={customer.id} data-id="element-819" data-mp="1ylrxnxpc0o9t">
                        {customer.customerNo} — {customer.name}
                      </option>)}
                  </select>
                </label>
                {!customerId && <input value={manualCustomer} onChange={e => setManualCustomer(e.target.value)} placeholder="…eller skriv kundens navn" className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-820" data-mp="1auw1koduf6wr" />}
              </div>

              <fieldset data-id="element-821" data-mp="37uo37irctph2">
                <legend className="mb-2 text-sm font-medium text-stone-700" data-id="element-822" data-mp="37rs965imnkxw">
                  Start fra skabelon
                </legend>
                <div className="space-y-2" data-id="element-823" data-mp="4do2j2zs8h3c">
                  {qualityTemplates.map(template => {
                const isActive = templateId === template.id;
                return <label key={template.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${isActive ? 'border-brand-300 bg-brand-50' : 'border-stone-200 hover:bg-stone-50'}`} data-id="element-824" data-mp="3rhcdb2nrs4zl">
                        <input type="radio" name="ks-template" value={template.id} checked={isActive} onChange={() => setTemplateId(template.id)} className="mt-0.5 h-4 w-4 flex-shrink-0 accent-brand-600" data-id="element-825" data-mp="qm38ecfqrrwl" />
                        <span className="min-w-0" data-id="element-826" data-mp="19cb78icerw6z">
                          <span className="block text-sm font-semibold text-stone-900" data-id="element-827" data-mp="19bjgbktramn1">
                            {template.label}
                          </span>
                          <span className="block text-xs text-stone-500" data-id="element-828" data-mp="1j9z5vjd4xnuf">
                            {template.description}
                          </span>
                        </span>
                      </label>;
              })}
                </div>
              </fieldset>
            </div>

            <footer className="flex gap-3 border-t border-stone-200 px-5 py-4" data-id="element-829" data-mp="3p3ytnjtsq6bj">
              <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50" data-id="element-830" data-mp="3665g7b7b4zfb">
                Annuller
              </button>
              <button type="submit" disabled={!canSubmit} className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-id="element-831" data-mp="3ayq3dtvpke9v">
                Opret
              </button>
            </footer>
          </motion.form>
        </div>}
    </AnimatePresence>;
}